import React, { Fragment, useCallback, useEffect, useState, useRef } from "react";
import Breadcrumb from "../common/breadcrumb";
import { Button, Card, CardBody, CardHeader, Col, Container, Row, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Spinner, } from "reactstrap";
import { fetchBranches, getCategoryListing, getMerchantListing, getProductListing } from "../../utils/apiCalls";
import { getProductImageUrl, FALLBACK_IMAGE } from "../../utils/imageUtils";
import { Link } from 'react-router-dom';
import { Plus, ShoppingCart } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { addProductInCart, setMerchant, clearCartProducts, setOrderMetaData } from '../../redux/cart/cartSlice';
import { isEqual } from 'lodash';
import "./create-order.css";

const CreateOrder = () => {
    const dispatch = useDispatch();

    const userType = localStorage.getItem("userType");
    const merchantId = localStorage.getItem("MerchantId");
    const { cartItems, branchId, orderType, merchantId: cartMerchantId } = useSelector(state => state.cart);

    const [isLoading, setIsLoading] = useState(false);
    const [merchantList, setMerchantList] = useState([]);
    const [branches, setBranches] = useState([]);
    const [displayedBranches, setDisplayedBranches] = useState([]);
    const [categories, setCategories] = useState(null);
    const [productData, setProductData] = useState([]);
    const [selectedMerchant, setSelectedMerchant] = useState(userType === "merchant" ? merchantId : cartMerchantId || 0);
    const [isModifierModalOpen, setIsModifierModalOpen] = useState(false);
    const [selectedProductForModifiers, setSelectedProductForModifiers] = useState(null);
    const [selectedModifiers, setSelectedModifiers] = useState({});
    const [productNote, setProductNote] = useState('');
    const [activeCategory, setActiveCategory] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(branchId || '');
    const [selectedOrderType, setSelectedOrderType] = useState(orderType || '');
    const [searchTerm, setSearchTerm] = useState('');

    const productContainerRef = useRef(null);
    const categoryContainerRef = useRef(null);
    const categoryRefs = useRef({});
    const productSectionRefs = useRef({});
    const isCategoryClickScrolling = useRef(false);

    const orderTypes = ["Pickup", "Delivery", "Drivethrough", "Dinein"];

    // Fetch initial data on component mount
    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoading(true);
            try {
                if (userType !== "merchant") {
                    const merchants = await getMerchantListing();
                    setMerchantList(merchants);
                }
            } catch (error) {
                console.error("Error fetching initial data:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchInitialData();
    }, [userType]);

    const fetchBranchesData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetchBranches();
            setBranches(response.data);
            setDisplayedBranches(response.data); // Initially display all branches
        } catch (error) {
            console.error("Error fetching branches:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch branches on component mount
    useEffect(() => {
        fetchBranchesData();
    }, [fetchBranchesData]);

    const handleMerchantChange = (e) => {
        const newMerchantId = e.target.value;

        // If the cart has items and the merchant is changing, show confirmation
        if (cartItems.length > 0 && cartMerchantId && newMerchantId !== cartMerchantId) {
            const confirmed = window.confirm(
                "Changing the merchant will clear your current cart. Do you want to proceed?"
            );

            if (!confirmed) {
                // User canceled → do nothing, revert dropdown to previous merchant
                return;
            }

            // User confirmed → clear cart and proceed
            dispatch(clearCartProducts());
        }

        // Update merchant
        setSelectedMerchant(newMerchantId);
        dispatch(setMerchant({ merchantId: newMerchantId }));

        console.log("newMerchantId", newMerchantId)
        // Filter branches based on selected merchant
        if (newMerchantId === "all") {
            setDisplayedBranches(branches); // Display all branches
        } else {
            const filteredBranches = branches?.filter(
                (branch) => branch?.MerchantId == newMerchantId && branch?.active
            );
            setDisplayedBranches(filteredBranches); // Display filtered branches
        }
    };

    // Fetch categories and products when a merchant is selected
    useEffect(() => {
        const fetchDataForMerchant = async () => {
            if (selectedMerchant && selectedMerchant !== 0 && selectedMerchant !== 'all') {
                setIsLoading(true);
                try {
                    const merchantFilter = userType === "merchant" ? merchantId : selectedMerchant;
                    const [categoriesRes, productsRes] = await Promise.all([
                        getCategoryListing(merchantFilter),
                        getProductListing(merchantFilter)
                    ]);
                    setCategories(categoriesRes || []);
                    setProductData(productsRes || []);
                } catch (error) {
                    console.error("Error fetching merchant data:", error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                // Clear data if no merchant is selected
                setCategories(null);
                setProductData([]);
            }
        };

        fetchDataForMerchant();
    }, [selectedMerchant, userType, merchantId]);

    const categoriesWithProducts = searchTerm
        ? categories?.filter(category =>
            productData.some(product =>
                product.CategoryId === category.id &&
                product.title.toLowerCase().includes(searchTerm.toLowerCase())
            )
        )
        : categories?.filter(category =>
            productData.some(product => product.CategoryId === category.id)
        );

    // const categoriesWithProducts = categories?.filter(category =>
    //     productData.some(product => product.CategoryId === category.id)
    // );

    useEffect(() => {
        if (categories && categories.length > 0 && !activeCategory) {
            setActiveCategory(categories[0].id);
        }
    }, [categories, activeCategory]);

    const handleCategoryClick = (categoryId) => {
        isCategoryClickScrolling.current = true;
        setActiveCategory(categoryId);
        const container = productContainerRef.current;
        const section = productSectionRefs.current[categoryId];

        if (container && section) {
            // Calculate the target scroll position relative to the container
            const sectionTop = section.offsetTop;
            container.scrollTo({
                top: sectionTop,
                behavior: 'smooth'
            });
        }
        setTimeout(() => {
            isCategoryClickScrolling.current = false;
        }, 1000); // Adjust timeout as needed
    };

    const handleProductScroll = useCallback(() => {
        if (isCategoryClickScrolling.current || searchTerm) return;
        const container = productContainerRef.current;
        if (!container || !categoriesWithProducts) return;

        const containerTop = container.getBoundingClientRect().top;
        let newActiveCategory = categoriesWithProducts[0]?.id;

        for (const category of categoriesWithProducts) {
            const section = productSectionRefs.current[category.id];
            if (section && section.getBoundingClientRect().top <= containerTop + 10) { // 10px threshold
                newActiveCategory = category.id;
            } else {
                break;
            }
        }

        if (activeCategory !== newActiveCategory) {
            setActiveCategory(newActiveCategory);
        }
    }, [activeCategory, categoriesWithProducts, searchTerm]);

    useEffect(() => {
        if (activeCategory && categoryRefs.current[activeCategory]) {
            categoryRefs.current[activeCategory].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start'
            });
        }
    }, [activeCategory]);


    const dropdownStyle = {
        padding: "10px",
        fontSize: "16px",
        borderRadius: "5px",
        border: "1px solid #009689",
    };

    const addToCart = (product, selectedModifiers = {}, note = '') => {
        const modifierIds = getModifierIds(selectedModifiers);
        const existingCartItem = cartItems.find(item =>
            item.id === product.id && isEqual(item.modifierIds, modifierIds)
        );

        const qty = existingCartItem ? existingCartItem.quantity + 1 : 1;

        dispatch(addProductInCart({ product, qty, note, modifiers: selectedModifiers }));
        if (isModifierModalOpen) toggleModifierModal();
        setProductNote('');
    };

    const getModifierIds = (selectedModifiers) => {
        return Object.values(selectedModifiers)
            .flat()
            .map(m => m.id)
            .sort((a, b) => a - b);
    };

    const handleProductClick = async (product) => {
        if (!branchId || !orderType) {
            alert('Please select a branch and order type before placing an order.');
            return;
        }

        if (product.Modifiers && product.Modifiers.length > 0) {
            setSelectedProductForModifiers(product);
            setSelectedModifiers({}); // Clear previous selections
            setProductNote('');
            setIsModifierModalOpen(true);
        } else {
            addToCart(product);
        }
    };

    const toggleModifierModal = () => {
        setIsModifierModalOpen(!isModifierModalOpen);
        setSelectedModifiers({}); // Reset on close
        setProductNote('');
    };

    const handleOrderInfoChange = (type, value) => {
        const newBranchId = type === 'branch' ? value : selectedBranch;
        const newOrderType = type === 'orderType' ? value : selectedOrderType;
        setSelectedBranch(newBranchId);
        setSelectedOrderType(newOrderType);
        dispatch(setOrderMetaData({ branchId: newBranchId, orderType: newOrderType }));
    };

    const handleModifierChange = (modifierCategory, modifier) => {
        const categoryId = modifierCategory.category_id;

        if (modifierCategory.nature === 'multiple') {
            setSelectedModifiers(prev => {
                const existingModifiers = prev[categoryId] || []; // this will be an array of modifier objects
                const existingIndex = existingModifiers.findIndex(m => m.id === modifier.id);
                let newModifiers;

                if (existingIndex > -1) {
                    // If selected, remove it from the array
                    newModifiers = existingModifiers.filter(m => m.id !== modifier.id);
                } else {
                    // If not selected, add the full modifier object
                    newModifiers = [...existingModifiers, modifier];
                }

                return { ...prev, [categoryId]: newModifiers };
            });
        } else { // 'single'
            setSelectedModifiers(prev => ({
                ...prev,
                // For single selection, we store the modifier object directly
                [categoryId]: [modifier],
            }));
        }
    };

    const filteredProducts = (categoryId) => {
        return productData
            .filter(p => p.CategoryId === categoryId)
            .filter(p => p.title.toLowerCase().includes(searchTerm.toLowerCase()));
    };

    return (
        <Fragment>
            <Breadcrumb title="Create Order" parent="Orders">
                <Link
                    to="/orders/cart"
                    className="btn btn-primary cart-btn-header d-flex justify-content-center align-items-center"
                >
                    <ShoppingCart size={18} />
                    <span className="ms-2">View Cart</span>
                    {cartItems.length > 0 && (
                        <span className="badge rounded-pill bg-danger ms-2">
                            {cartItems?.length}
                        </span>
                    )}
                </Link>
            </Breadcrumb>


            <Container fluid={true}>
                <Card>
                    <CardHeader>
                        <Row>
                            <Col className="d-flex justify-content-end align-items-center gap-3">
                                {userType !== "merchant" ? (
                                    <select
                                        value={selectedMerchant}
                                        onChange={handleMerchantChange}
                                        className="form-select"
                                        style={dropdownStyle} >
                                        <option value={0}>Select Merchant</option>
                                        {merchantList?.map((merchant) => (
                                            <option key={merchant.id} value={merchant.id}>
                                                {merchant?.name}
                                            </option>
                                        ))}
                                    </select>
                                ) : null}

                                <select
                                    value={selectedBranch}
                                    onChange={(e) => handleOrderInfoChange('branch', e.target.value)}
                                    className="form-select"
                                    style={dropdownStyle} >
                                    <option value="0">Select Branch</option>
                                    {displayedBranches?.map((branch) => (<option key={branch.id} value={branch.id}>{branch.name}</option>))}
                                </select>

                                <select
                                    value={selectedOrderType}
                                    onChange={(e) => handleOrderInfoChange('orderType', e.target.value)}
                                    className="form-select"
                                    style={dropdownStyle} >
                                    <option value="">Select Order Type</option>
                                    {orderTypes.map((type) => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>

                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search products..."
                                    className="form-control"
                                    style={dropdownStyle}
                                />
                            </Col>
                        </Row>
                    </CardHeader>
                    <CardBody className="pt-2">
                        {categoriesWithProducts && categoriesWithProducts.length > 0 ? (
                            // <CardBody className="pt-2 card-body-with-sticky-categories">
                            <>
                                <div className="horizontal-categories-container mb-4" ref={categoryContainerRef}>
                                    {categoriesWithProducts.map((category) => (
                                        <Button
                                            key={category.id}
                                            innerRef={(el) => (categoryRefs.current[category.id] = el)}
                                            className={`category-item-horizontal ${!searchTerm && activeCategory === category.id ? 'active' : ''}`}
                                            onClick={() => handleCategoryClick(category.id)}
                                            color={activeCategory === category.id ? 'primary' : 'light'}
                                        >
                                            {category.name}
                                        </Button>
                                    ))}
                                </div>

                                <div ref={productContainerRef} className="product-container" onScroll={handleProductScroll}>
                                    {categoriesWithProducts?.map((category) => (
                                        <div
                                            key={category.id}
                                            ref={(el) => (productSectionRefs.current[category.id] = el)}
                                            className="product-section"
                                        >
                                            <h5 className="product-category mb-3">{category.name}</h5>

                                            <Row className="g-3">
                                                {filteredProducts(category.id).map((product) => (
                                                    <Col className="product-col" key={product.id}>
                                                        <Card
                                                            className="custom-product-card"
                                                            onClick={() => handleProductClick(product)}
                                                        >
                                                            {/* IMAGE SECTION */}
                                                            <div className="custom-product-img-wrapper">
                                                                <img
                                                                    alt={product.title}
                                                                    src={
                                                                        getProductImageUrl(product)
                                                                    }
                                                                    onError={(e) => {
                                                                        e.target.src = FALLBACK_IMAGE;
                                                                        e.target.classList.add("no-cover");
                                                                    }}
                                                                    className="custom-product-img"
                                                                />
                                                            </div>

                                                            {/* TEXT SECTION */}
                                                            <div className="product-detail">
                                                                <h6 className="product-title">{product.title}</h6>
                                                                <div className="product-bottom">
                                                                    <span className="product-price">
                                                                        SAR {product.price.toFixed(2)}
                                                                    </span>
                                                                    <div className="add-btn">
                                                                        <Plus size={18} color="#fff" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Card>
                                                    </Col>
                                                ))}
                                            </Row>


                                            {/* <Row className="g-3">
                                                {productData
                                                    ?.filter((p) => p.CategoryId === category.id)
                                                    .map((product) => (
                                                        <Col
                                                            className="product-col"
                                                            key={product.id}>
                                                            <Card
                                                                className="custom-product-card"
                                                                onClick={() => handleProductClick(product)}
                                                            >
                                                             
                                                                <div className="custom-product-img-wrapper">
                                                                    <img
                                                                        alt={product.title}
                                                                        src={
                                                                            product?.image1
                                                                                ? process.env.REACT_APP_URL + product.image1
                                                                                : product?.merchant_image
                                                                                    ? product?.merchant_image
                                                                                    : "fallback"
                                                                        }
                                                                        onError={(e) => {
                                                                            e.target.src = "https://cdn3.iconfinder.com/data/icons/linecons-free-vector-icons-pack/32/camera-512.png";
                                                                            e.target.classList.add("no-cover");
                                                                        }}
                                                                        className="custom-product-img"
                                                                    />
                                                                </div>

                                                              
                                                                <div className="product-detail">
                                                                    <h6 className="product-title">{product.title}</h6>
                                                                    <div className="product-bottom">
                                                                        <span className="product-price">
                                                                            SAR {product.price.toFixed(2)}
                                                                        </span>

                                                                        <div className="add-btn">
                                                                            <Plus size={18} color="#fff" />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </Card>
                                                        </Col>
                                                    ))}
                                            </Row> */}
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : <p className="text-center">Select a merchant and branch to see products.</p>}
                    </CardBody>
                </Card>

                <Modal isOpen={isModifierModalOpen} toggle={() => toggleModifierModal(false)} centered size="lg">
                    <ModalHeader toggle={() => toggleModifierModal(false)}>
                        Customize {selectedProductForModifiers?.title}
                    </ModalHeader>
                    <ModalBody>
                        <Row>
                            <Col md="5">
                                {selectedProductForModifiers && (
                                    <div className="product-info-modal">
                                        <img
                                            src={
                                                selectedProductForModifiers.image1
                                                    ? process.env.REACT_APP_URL + selectedProductForModifiers.image1
                                                    : "https://cdn3.iconfinder.com/data/icons/linecons-free-vector-icons-pack/32/camera-512.png"
                                            }
                                            alt={selectedProductForModifiers.title}
                                            className="img-fluid rounded mb-3"
                                        />
                                        <h4>{selectedProductForModifiers.title}</h4>
                                        <p className="text-muted">{selectedProductForModifiers.description}</p>
                                        <p className="h5">SAR {selectedProductForModifiers.price.toFixed(2)}</p>
                                    </div>
                                )}
                            </Col>
                            <Col md="7">
                                <div className="modifiers-and-notes-section">
                                    {selectedProductForModifiers?.Modifiers?.length > 0 ? (
                                        selectedProductForModifiers?.Modifiers?.map(category => (
                                            <div key={category.category_id} className="modifier-category-section">
                                                <h5>{category.category_name} {category.isRequired && <span className="text-danger">*</span>} ({category.nature})</h5>
                                                <FormGroup>
                                                    {category?.modifiers?.map(modifier => (
                                                        <div key={modifier.id} className="modifier-item">
                                                            <Input
                                                                type={category.nature === 'single' ? 'radio' : 'checkbox'}
                                                                id={`modifier-${modifier.id}`}
                                                                name={`modifier-cat-${category.category_id}`}
                                                                checked={selectedModifiers[category.category_id]?.some(m => m.id === modifier.id)}
                                                                onChange={() => handleModifierChange(category, modifier)}
                                                            />
                                                            <Label for={`modifier-${modifier.id}`} className="modifier-label">
                                                                <span>{modifier?.title}</span>
                                                                <span className="modifier-price">+SAR {modifier?.price?.toFixed(2)}</span>
                                                            </Label>
                                                        </div>
                                                    ))}
                                                </FormGroup>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-center">No modifiers available for this product.</p>
                                    )}
                                    <FormGroup>
                                        <Label for="productNote">Notes</Label>
                                        <Input
                                            type="textarea"
                                            name="productNote"
                                            id="productNote"
                                            placeholder="Any special instructions?"
                                            value={productNote}
                                            onChange={(e) => setProductNote(e.target.value)}
                                        />
                                    </FormGroup>
                                </div>
                            </Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => toggleModifierModal(false)}>Cancel</Button>
                        <Button color="primary" onClick={() => addToCart(selectedProductForModifiers, selectedModifiers, productNote)}>
                            Add to Cart
                        </Button>
                    </ModalFooter>
                </Modal>

            </Container>
        </Fragment>
    );
};

export default CreateOrder;